import { Link as RouterLink, useLocation } from 'react-router-dom';
import { useMobileDrawer } from '../contexts/MobileDrawerContext';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import Avatar from '@mui/material/Avatar';
import { styled, alpha } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PersonIcon from '@mui/icons-material/Person';
import LogoutIcon from '@mui/icons-material/Logout';
import LoginIcon from '@mui/icons-material/Login';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PeopleIcon from '@mui/icons-material/People';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import StoreIcon from '@mui/icons-material/Store';
import StorefrontIcon from '@mui/icons-material/Storefront';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import CategoryIcon from '@mui/icons-material/Category';
import DiscountIcon from '@mui/icons-material/Discount';
import ReceiptIcon from '@mui/icons-material/Receipt';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import SettingsIcon from '@mui/icons-material/Settings';
import FlagIcon from '@mui/icons-material/Flag';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CardMembershipIcon from '@mui/icons-material/CardMembership';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EventNoteIcon from '@mui/icons-material/EventNote';
import StarIcon from '@mui/icons-material/Star';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import CalendarViewMonthIcon from '@mui/icons-material/CalendarViewMonth';
import { Role, FEATURE_FLAGS } from '@lov/shared';
import { useFeatureFlag } from '../features/feature-flags/hooks/useFeatureFlags';
import { ThemeToggle } from './ThemeToggle';
import { NotificationCenter } from '../features/notifications';
import { Logo } from './shared';

interface MobileDrawerUser {
  firstName?: string;
  lastName?: string;
  email?: string;
  role?: Role;
}

interface MobileDrawerProps {
  isAuthenticated: boolean;
  user?: MobileDrawerUser | null;
  onLogout: () => void;
  isLoggingOut?: boolean;
}

const DrawerContent = styled(Box)(({ theme }) => ({
  width: 300,
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: theme.palette.background.paper,
}));

const DrawerHeader = styled(Box)(({ theme }) => ({
  padding: '24px 20px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  borderBottom: `1px solid ${theme.palette.surface.glassBorder}`,
}));

const UserSection = styled(Box)(({ theme }) => ({
  padding: '20px',
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  backgroundColor: theme.palette.background.default,
  borderBottom: `1px solid ${theme.palette.surface.glassBorder}`,
}));

const NavList = styled(List)({
  padding: '12px 8px',
  flex: 1,
  overflowY: 'auto',
});

const StyledListItemButton = styled(ListItemButton, {
  shouldForwardProp: (prop) => prop !== 'isActive',
})<{ isActive?: boolean; component?: React.ElementType; to?: string }>(({ theme, isActive }) => ({
  borderRadius: 10,
  margin: '2px 0',
  padding: '8px 16px',
  transition: 'all 0.2s ease',
  backgroundColor: isActive ? alpha(theme.palette.success.main, 0.08) : 'transparent',
  borderLeft: isActive ? `3px solid ${theme.palette.success.main}` : '3px solid transparent',
  '&:hover': {
    backgroundColor: isActive
      ? alpha(theme.palette.success.main, 0.12)
      : theme.palette.surface.hover,
  },
  '& .MuiListItemIcon-root': {
    color: isActive ? theme.palette.success.main : theme.palette.text.secondary,
    minWidth: 40,
  },
  '& .MuiListItemText-primary': {
    fontFamily: '"IBM Plex Sans", sans-serif',
    color: isActive ? theme.palette.success.dark : theme.palette.text.primary,
    fontWeight: isActive ? 600 : 500,
  },
}));

const FooterSection = styled(Box)(({ theme }) => ({
  padding: '16px 20px',
  borderTop: `1px solid ${theme.palette.surface.glassBorder}`,
}));

/**
 * Mobile drawer navigation for Vendor and Admin roles
 * Customers use BottomNavigation instead (via CustomerLayout)
 */
export function MobileDrawer({
  isAuthenticated,
  user,
  onLogout,
  isLoggingOut = false,
}: MobileDrawerProps) {
  const { isOpen: open, toggleDrawer, closeDrawer } = useMobileDrawer();
  const location = useLocation();
  const { data: vendorBillingFlag } = useFeatureFlag(FEATURE_FLAGS.VENDOR_BILLING);

  // Don't render for customers - they use bottom navigation
  if (isAuthenticated && user?.role === Role.CUSTOMER) {
    return null;
  }

  const isActive = (path: string) => location.pathname === path;

  type NavLink = { label: string; icon: React.ReactNode; to: string; disabled?: boolean };

  // Base authenticated links for Vendor/Admin (not customers)
  const authenticatedLinks: NavLink[] = [
    {
      label: 'Dashboard',
      icon: <DashboardIcon />,
      to: user?.role === Role.SUPER_ADMIN ? '/dashboard' : '/vendor/dashboard',
    },
    { label: 'Profile', icon: <PersonIcon />, to: '/profile' },
  ];

  // Role-based links
  const roleBasedLinks: NavLink[] = [];

  if (user?.role === Role.SUPER_ADMIN) {
    roleBasedLinks.push(
      { label: 'Bookings', icon: <EventNoteIcon />, to: '/admin/bookings' },
      { label: 'Customers', icon: <PeopleIcon />, to: '/admin/users' },
      { label: 'Staff', icon: <PeopleIcon />, to: '/admin/staff' },
      { label: 'Vendors', icon: <VerifiedUserIcon />, to: '/admin/vendors' },
      { label: 'Pending Approval', icon: <VerifiedUserIcon />, to: '/admin/vendors/pending' },
      { label: 'Venue Enquiries', icon: <VerifiedUserIcon />, to: '/admin/vendor-enquiries' },
      { label: 'Venues', icon: <StoreIcon />, to: '/admin/venues' },
      { label: 'Create Venue', icon: <StoreIcon />, to: '/admin/venues/create' },
      { label: 'Pending Approval', icon: <StoreIcon />, to: '/admin/venues/pending' },
      { label: 'Locations', icon: <LocationCityIcon />, to: '/admin/locations' },
      { label: 'Inventory', icon: <Inventory2Icon />, to: '/admin/inventory' },
      { label: 'Inventory Requests', icon: <Inventory2Icon />, to: '/admin/inventory/requests' },
      { label: 'Promotions', icon: <DiscountIcon />, to: '/admin/coupons' },
      { label: 'Analytics', icon: <AnalyticsIcon />, to: '/admin/analytics' },
      { label: 'Payouts', icon: <AccountBalanceIcon />, to: '/admin/payouts' },
      { label: 'Subscriptions', icon: <CardMembershipIcon />, to: '/admin/subscriptions' },
      {
        label: 'Subscription Plans',
        icon: <CardMembershipIcon />,
        to: '/admin/subscription-plans',
      },
      { label: 'Capability Modules', icon: <SettingsIcon />, to: '/admin/modules' },
      { label: 'Amenities', icon: <SettingsIcon />, to: '/admin/master-data?tab=1' },
      { label: 'Check-In Settings', icon: <CalendarMonthIcon />, to: '/admin/check-in-config' },
      { label: 'Feature Flags', icon: <FlagIcon />, to: '/admin/feature-flags' },
      { label: 'Cron Monitoring', icon: <AccessTimeIcon />, to: '/admin/cron-monitoring' },
      { label: 'SSL Certificates', icon: <SettingsIcon />, to: '/admin/ssl' },
      { label: 'System Settings', icon: <SettingsIcon />, to: '/admin/settings' }
    );
  }

  if (user?.role === Role.VENDOR || user?.role === Role.VENDOR_STAFF) {
    roleBasedLinks.push(
      { label: 'Bookings', icon: <CalendarTodayIcon />, to: '/vendor/bookings' },
      { label: 'Calendar', icon: <CalendarViewMonthIcon />, to: '/vendor/bookings/calendar' },
      {
        label: 'Active Sessions',
        icon: <AccessTimeIcon />,
        to: '/vendor/bookings/active-sessions',
      },
      { label: 'Check-In', icon: <HowToRegIcon />, to: '/vendor/check-in' },
      { label: 'Pending Payments', icon: <ReceiptIcon />, to: '/vendor/unpaid-bookings' },
      { label: 'Venues', icon: <StorefrontIcon />, to: '/vendor/venues' },
      { label: 'Inventory Requests', icon: <CategoryIcon />, to: '/vendor/inventory/requests' },
      { label: 'Promotions', icon: <DiscountIcon />, to: '/vendor/coupons' },
      { label: 'Food Menu', icon: <RestaurantMenuIcon />, to: '/vendor/food-menu' },
      { label: 'Food Offers', icon: <RestaurantMenuIcon />, to: '/vendor/food-offers' },
      { label: 'Analytics', icon: <AnalyticsIcon />, to: '/vendor/analytics' },
      { label: 'Add-on Performance', icon: <AnalyticsIcon />, to: '/vendor/addon-analytics' },
      { label: 'Food Analytics', icon: <AnalyticsIcon />, to: '/vendor/food-analytics' },
      { label: 'Customers', icon: <PeopleIcon />, to: '/vendor/customers' },
      { label: 'Team', icon: <PeopleIcon />, to: '/vendor/staff' },
      { label: 'Payouts', icon: <AccountBalanceIcon />, to: '/vendor/payouts' },
      { label: 'Tax Invoices', icon: <ReceiptLongIcon />, to: '/vendor/tax-invoices' },
      { label: 'Commission Invoices', icon: <ReceiptIcon />, to: '/vendor/commission-invoices' },
      { label: 'Tax Summary', icon: <ReceiptIcon />, to: '/vendor/tax-summary' },
      { label: 'Reviews', icon: <StarIcon />, to: '/vendor/reviews' },
      ...(vendorBillingFlag?.enabled
        ? [
            {
              label: 'Billing & Plans',
              icon: <CardMembershipIcon />,
              to: '/vendor/subscription/plans',
              disabled: true,
            },
          ]
        : []),
      { label: 'Settings', icon: <SettingsIcon />, to: '/vendor/settings' }
    );
  }

  // Combine all authenticated links
  const allAuthenticatedLinks = [...authenticatedLinks, ...roleBasedLinks];

  const guestLinks = [
    { label: 'Log In', icon: <LoginIcon />, to: '/login' },
    { label: 'Sign Up', icon: <PersonAddIcon />, to: '/register' },
  ];

  return (
    <>
      <IconButton
        edge="end"
        aria-label="open menu"
        onClick={toggleDrawer}
        sx={{
          display: { xs: 'flex', lg: 'none' },
          color: 'text.primary',
          '&:hover': {
            backgroundColor: 'surface.hover',
          },
        }}
      >
        <MenuIcon />
      </IconButton>

      <Drawer
        anchor="right"
        open={open}
        onClose={closeDrawer}
        PaperProps={{
          sx: {
            backgroundColor: 'transparent',
            boxShadow: 'none',
          },
        }}
      >
        <DrawerContent>
          <DrawerHeader>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Logo size="medium" />
              <Typography
                variant="h6"
                sx={{
                  fontFamily: '"IBM Plex Mono", monospace',
                  fontWeight: 600,
                  color: 'text.primary',
                }}
              >
                Lord of Venues
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ThemeToggle size="medium" />
              {isAuthenticated && <NotificationCenter />}
              <IconButton onClick={closeDrawer} sx={{ color: 'text.secondary' }}>
                <CloseIcon />
              </IconButton>
            </Box>
          </DrawerHeader>

          {isAuthenticated && user && (
            <UserSection>
              <Avatar
                sx={{
                  width: 48,
                  height: 48,
                  bgcolor: (theme) => alpha(theme.palette.text.primary, 0.08),
                  color: 'text.primary',
                  fontWeight: 600,
                  fontFamily: '"IBM Plex Mono", monospace',
                }}
              >
                {user.firstName?.[0]}
                {user.lastName?.[0]}
              </Avatar>
              <Box>
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  sx={{ fontFamily: '"IBM Plex Sans", sans-serif' }}
                >
                  {user.firstName} {user.lastName}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontFamily: '"IBM Plex Sans", sans-serif' }}
                >
                  {user.email}
                </Typography>
              </Box>
            </UserSection>
          )}

          <NavList>
            {isAuthenticated
              ? allAuthenticatedLinks.map((link) =>
                  link.disabled ? (
                    <ListItem key={link.to} disablePadding>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          borderRadius: '10px',
                          margin: '2px 0',
                          padding: '8px 16px',
                          width: '100%',
                          cursor: 'default',
                          color: 'text.disabled',
                          '& .MuiListItemIcon-root': {
                            color: 'text.disabled',
                            minWidth: 40,
                          },
                        }}
                      >
                        <ListItemIcon sx={{ color: 'text.disabled', minWidth: 40 }}>
                          {link.icon}
                        </ListItemIcon>
                        <ListItemText
                          primary={link.label}
                          sx={{
                            '& .MuiListItemText-primary': {
                              fontFamily: '"IBM Plex Sans", sans-serif',
                              color: 'text.disabled',
                              fontWeight: 500,
                            },
                          }}
                        />
                        <Chip
                          label="Coming Soon"
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: '0.625rem',
                            fontWeight: 600,
                            letterSpacing: '0.02em',
                            ml: 1,
                          }}
                        />
                      </Box>
                    </ListItem>
                  ) : (
                    <ListItem key={link.to} disablePadding>
                      <StyledListItemButton
                        component={RouterLink}
                        to={link.to}
                        onClick={closeDrawer}
                        isActive={isActive(link.to)}
                      >
                        <ListItemIcon>{link.icon}</ListItemIcon>
                        <ListItemText primary={link.label} />
                      </StyledListItemButton>
                    </ListItem>
                  )
                )
              : guestLinks.map((link) => (
                  <ListItem key={link.to} disablePadding>
                    <StyledListItemButton
                      component={RouterLink}
                      to={link.to}
                      onClick={closeDrawer}
                      isActive={isActive(link.to)}
                    >
                      <ListItemIcon>{link.icon}</ListItemIcon>
                      <ListItemText primary={link.label} />
                    </StyledListItemButton>
                  </ListItem>
                ))}
          </NavList>

          {isAuthenticated && (
            <FooterSection>
              <StyledListItemButton
                onClick={() => {
                  onLogout();
                  closeDrawer();
                }}
                disabled={isLoggingOut}
                sx={{
                  color: 'error.main',
                  '& .MuiListItemIcon-root': {
                    color: 'error.main',
                  },
                  '&:hover': {
                    backgroundColor: (theme) => alpha(theme.palette.error.main, 0.1),
                  },
                }}
              >
                <ListItemIcon>
                  <LogoutIcon />
                </ListItemIcon>
                <ListItemText primary={isLoggingOut ? 'Logging out...' : 'Logout'} />
              </StyledListItemButton>
            </FooterSection>
          )}
        </DrawerContent>
      </Drawer>
    </>
  );
}
